'use client';

import { useLocale, useTranslations } from 'next-intl';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  checkCreateUserOfflineAvatar,
  createUserOfflineAvatar,
  deleteUserOfflineAvatar,
  type InferV1OfflineAvatar,
  listPublicOfflineAvatars,
  listUserOfflineAvatars,
  updateUserOfflineAvatar,
} from '@/app/apis';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { Loading } from '@/app/components/icons/Loading';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { PhotoUploadTipsDialog } from '@/app/components/shared/PhotoUploadTipsDialog';
import { VoiceSelectionDialog } from '@/app/components/shared/VoiceSelectionDialog';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/app/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { toast } from '@/app/components/ui/use-toast';
import { TTS_VOICE_MAP } from '@/app/constants';
import { hasPhotoUploadTipsConsent, savePhotoUploadTipsConsent, useFetch, useGlobalModal } from '@/app/hooks';
import type { FormImagePrompt, GetSelectedImageResult, ImageUploadFile, OfflineAvatarAsset } from '@/app/types';
import { cn } from '@/app/utils';
import { offlineAvatarHasBuiltInAudio, parseOfflineAvatarAudioAttrs } from '@/app/utils/client';
import { ToolMediaUpload } from '../../_components/ToolMediaUpload';
import { ToolMediaUploadHeader } from '../../_components/ToolMediaUploadHeader';
import { createToolImagePromptFromUploadFile } from '../../_utils';

const OFFLINE_AVATAR_PAGE_SIZE = 50;
const OFFLINE_AVATAR_ASSET_TABS = ['user', 'public'] as const;
const MAX_CREATE_NAME_ZH = 10;
const MAX_CREATE_NAME_OTHER = 50;
const MAX_CREATE_DESCRIPTION = 5000;

type OfflineAvatarAssetTab = (typeof OFFLINE_AVATAR_ASSET_TABS)[number];

interface OfflineAvatarAssetSelectorProps {
  title: string;
  selectedAvatar?: OfflineAvatarAsset;
  userId?: string;
  maxImageFileSize: number;
  getSelectedImage: (url: string) => Promise<GetSelectedImageResult>;
  onSelect: (avatar: OfflineAvatarAsset | undefined) => void;
}

type OfflineAvatarWithAudioFields = InferV1OfflineAvatar & {
  has_audio?: boolean;
  with_audio?: boolean;
};

const parseOfflineAvatarAttrs = (attrsJson?: string): Pick<OfflineAvatarWithAudioFields, 'with_audio'> => {
  const attrs = parseOfflineAvatarAudioAttrs(attrsJson);
  return typeof attrs.with_audio === 'boolean' ? { with_audio: attrs.with_audio } : {};
};

const toOfflineAvatarAsset = (avatar: InferV1OfflineAvatar): OfflineAvatarAsset => {
  const attrs = parseOfflineAvatarAttrs(avatar.attrs_json);
  const avatarWithAudio = avatar as OfflineAvatarWithAudioFields;

  return {
    id: avatar.id,
    name: avatar.name,
    description: avatar.description,
    live_image: avatar.live_image,
    voice_id: avatar.voice_id,
    avatar_type: avatar.avatar_type,
    created_at: avatar.created_at,
    has_audio: offlineAvatarHasBuiltInAudio(avatarWithAudio),
    with_audio: avatarWithAudio.with_audio ?? attrs.with_audio,
  };
};

const getLatestOfflineAvatar = (avatars: OfflineAvatarAsset[]): OfflineAvatarAsset | undefined =>
  avatars.reduce<OfflineAvatarAsset | undefined>((latest, avatar) => {
    if (!latest) return avatar;

    const avatarCreatedAt = Date.parse(avatar.created_at ?? '');
    const latestCreatedAt = Date.parse(latest.created_at ?? '');
    if (!Number.isFinite(avatarCreatedAt)) return latest;
    if (!Number.isFinite(latestCreatedAt) || avatarCreatedAt > latestCreatedAt) return avatar;
    return latest;
  }, undefined);

const OfflineAvatarCard: FC<{
  avatar: OfflineAvatarAsset;
  selected: boolean;
  onSelect: () => void;
  moreActionsLabel?: string;
  editLabel?: string;
  deleteLabel?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}> = ({ avatar, selected, onSelect, moreActionsLabel, editLabel, deleteLabel, onEdit, onDelete }) => {
  return (
    <div
      className={cn(
        'relative aspect-square w-full rounded-12 border bg-system-white04 transition-colors',
        selected ? 'border-system-white' : 'border-transparent hover:border-system-white24',
      )}
    >
      <button
        type="button"
        aria-label={avatar.name || 'Offline avatar'}
        className="size-full overflow-hidden rounded-[11px]"
        aria-pressed={selected}
        onClick={onSelect}
      >
        {avatar.live_image ? (
          <img src={avatar.live_image} alt="" className="size-full object-contain" />
        ) : (
          <span className="flex size-full items-center justify-center text-system-white32">
            <ProductLinePic className="size-6" />
          </span>
        )}
      </button>

      {onEdit && onDelete ? (
        <div className="group/menu absolute top-2 right-2 z-20">
          <button
            type="button"
            aria-label={moreActionsLabel}
            className="flex size-7 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 group-focus-within/menu:bg-black/60"
          >
            <BaseLineMore className="text-lg" />
          </button>
          <div className="pointer-events-none absolute top-full right-0 pt-1.5 opacity-0 transition-opacity duration-150 group-focus-within/menu:pointer-events-auto group-focus-within/menu:opacity-100 group-hover/menu:pointer-events-auto group-hover/menu:opacity-100">
            <div className="flex min-w-[104px] flex-col overflow-hidden rounded-12 bg-black/60 py-1 backdrop-blur-md">
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 text-left text-sm text-white transition-colors hover:bg-white/10"
                onClick={onEdit}
              >
                <BaseLineEdit className="shrink-0 text-lg" />
                <span>{editLabel}</span>
              </button>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 text-left text-[#FF4D4F] text-sm transition-colors hover:bg-[#FF4D4F]/10"
                onClick={onDelete}
              >
                <BaseLineDelete className="shrink-0 text-lg" />
                <span>{deleteLabel}</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const EmptyAssetState: FC<{ message: string }> = ({ message }) => (
  <div className="flex h-full items-center justify-center rounded-12 border border-system-white12 border-dashed bg-system-white04 px-4 text-center text-system-white40 text-xs">
    {message}
  </div>
);

const isServerUri = (uri?: string) => Boolean(uri && !uri.startsWith('temp://'));

const isOfflineAvatarQuotaExceeded = (error?: { code?: number; reason?: string }) =>
  error?.code === 429 || error?.reason === 'OfflineAvatarQuotaExceeded';

const getImagePreview = (imagePrompt?: FormImagePrompt | null) =>
  imagePrompt?.prompt?.selectedImagePreview ||
  imagePrompt?.prompt?.selectedImage ||
  imagePrompt?.prompt?.sourceImagePreview ||
  imagePrompt?.prompt?.sourceImage ||
  '';

export const OfflineAvatarAssetSelector: FC<OfflineAvatarAssetSelectorProps> = ({
  title,
  selectedAvatar,
  userId,
  maxImageFileSize,
  getSelectedImage,
  onSelect,
}) => {
  const t = useTranslations('tools.offlineAvatar.avatar');
  const tUploadTips = useTranslations('vidu-stream.createMyOwn');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const locale = useLocale();
  const createNameLimit = locale === 'zh' ? MAX_CREATE_NAME_ZH : MAX_CREATE_NAME_OTHER;
  const [activeAvatarTab, setActiveAvatarTab] = useState<OfflineAvatarAssetTab>('user');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createVoiceId, setCreateVoiceId] = useState('');
  const [createVoiceName, setCreateVoiceName] = useState('');
  const [createImagePrompt, setCreateImagePrompt] = useState<FormImagePrompt | null>(null);
  const [isVoiceDialogOpen, setIsVoiceDialogOpen] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState<OfflineAvatarAsset | null>(null);
  const [updatingAvatar, setUpdatingAvatar] = useState(false);
  const [checkingCreateAvatar, setCheckingCreateAvatar] = useState(false);
  const [showUploadTipsDialog, setShowUploadTipsDialog] = useState(false);
  const pendingUploadRef = useRef<(() => void) | null>(null);
  const hasInitializedDefaultRef = useRef(false);

  const resetCreateForm = () => {
    setCreateName('');
    setCreateDescription('');
    setCreateVoiceId('');
    setCreateVoiceName('');
    setCreateImagePrompt(null);
  };

  const {
    data: userAvatarsData,
    loading: userLoading,
    refresh: refreshUserAvatars,
  } = useFetch(
    () =>
      listUserOfflineAvatars({
        query: {
          'pager.page': 0,
          'pager.pagesz': OFFLINE_AVATAR_PAGE_SIZE,
        },
      }),
    {
      suppressUnauthorizedLoginDialog: true,
    },
  );

  const { data: publicAvatarsData, loading: publicLoading } = useFetch(() =>
    listPublicOfflineAvatars({
      query: {
        'pager.page': 0,
        'pager.pagesz': OFFLINE_AVATAR_PAGE_SIZE,
      },
    }),
  );

  const { run: createAvatar, loading: creatingAvatar } = useFetch(createUserOfflineAvatar, {
    manual: true,
    onSuccess: ({ data, error }) => {
      if (error || !data) {
        toast({
          type: 'error',
          message: error?.reason === 'OfflineAvatarQuotaExceeded' ? t('createQuotaExceeded') : t('createFailed'),
        });
        return;
      }

      const avatar = toOfflineAvatarAsset(data);
      onSelect(avatar);
      setIsCreateDialogOpen(false);
      resetCreateForm();
      void refreshUserAvatars();
      toast({ type: 'success', message: t('createSuccess') });
    },
  });

  const userAvatars = useMemo(
    () => (userAvatarsData?.data?.items || []).map(toOfflineAvatarAsset),
    [userAvatarsData?.data?.items],
  );
  const publicAvatars = useMemo(
    () => (publicAvatarsData?.data?.items || []).map(toOfflineAvatarAsset),
    [publicAvatarsData?.data?.items],
  );
  const activeAvatars = activeAvatarTab === 'user' ? userAvatars : publicAvatars;
  const activeLoading = activeAvatarTab === 'user' ? userLoading : publicLoading;

  useEffect(() => {
    if (hasInitializedDefaultRef.current) return;

    // Existing selections come from recreate/history state and must win over defaults.
    if (selectedAvatar?.id) {
      hasInitializedDefaultRef.current = true;
      return;
    }
    if (userLoading || publicLoading) return;

    const hasUserAvatars = userAvatars.length > 0;
    const nextTab: OfflineAvatarAssetTab = hasUserAvatars ? 'user' : 'public';
    const nextAvatar = getLatestOfflineAvatar(hasUserAvatars ? userAvatars : publicAvatars);

    hasInitializedDefaultRef.current = true;
    setActiveAvatarTab(nextTab);
    if (nextAvatar) onSelect(nextAvatar);
  }, [onSelect, publicAvatars, publicLoading, selectedAvatar?.id, userAvatars, userLoading]);

  const createImage =
    createImagePrompt?.prompt?.selectedImage?.trim() || createImagePrompt?.prompt?.sourceImage?.trim() || '';
  const createImagePreview = getImagePreview(createImagePrompt);
  const isCreateImageUploading = createImagePrompt?.status === 'uploading';
  const canCreateAvatar = isServerUri(createImage) && Boolean(createName.trim());
  const savingAvatar = creatingAvatar || updatingAvatar;

  const handleCreateAvatar = () => {
    if (!isServerUri(createImage)) {
      toast({ type: 'warning', message: t('missingCreateImage') });
      return;
    }

    const name = createName.trim();
    if (!name) {
      toast({ type: 'warning', message: t('missingCreateName') });
      return;
    }

    const description = createDescription.trim();
    createAvatar({
      body: {
        name,
        live_image: createImage,
        ...(description ? { description } : {}),
        ...(createVoiceId ? { voice_id: createVoiceId } : {}),
      },
    });
  };

  const handleUpdateAvatar = async () => {
    if (!editingAvatar || !isServerUri(createImage)) return;

    const name = createName.trim();
    if (!name) {
      toast({ type: 'warning', message: t('missingCreateName') });
      return;
    }

    const description = createDescription.trim();
    setUpdatingAvatar(true);
    try {
      const { data, error } = await updateUserOfflineAvatar({
        path: { id: editingAvatar.id },
        body: {
          id: editingAvatar.id,
          name,
          live_image: createImage,
          ...(description ? { description } : {}),
          ...(createVoiceId ? { voice_id: createVoiceId } : {}),
        },
      });
      if (error || !data) {
        toast({ type: 'error', message: t('updateFailed') });
        return;
      }

      if (selectedAvatar?.id === editingAvatar.id) {
        onSelect(toOfflineAvatarAsset(data));
      }
      setIsCreateDialogOpen(false);
      setEditingAvatar(null);
      resetCreateForm();
      await refreshUserAvatars();
      toast({ type: 'success', message: t('updateSuccess') });
    } catch {
      toast({ type: 'error', message: t('updateFailed') });
    } finally {
      setUpdatingAvatar(false);
    }
  };

  const handleCreateDialogOpenChange = (open: boolean) => {
    setIsCreateDialogOpen(open);
    if (!open) {
      setIsVoiceDialogOpen(false);
      setEditingAvatar(null);
      resetCreateForm();
    }
  };

  const handleCreateImageUpload = (file: ImageUploadFile) => {
    if (file.status === 'error' || file.status === 'removed') {
      setCreateImagePrompt(null);
      return;
    }

    setCreateImagePrompt(createToolImagePromptFromUploadFile(file));
  };

  const handleRequestCreateImageUpload = (openFileSelector: () => void) => {
    if (hasPhotoUploadTipsConsent(userId)) {
      openFileSelector();
      return;
    }

    pendingUploadRef.current = openFileSelector;
    setShowUploadTipsDialog(true);
  };

  const handleUploadTipsConfirm = () => {
    if (userId) {
      savePhotoUploadTipsConsent(userId);
    }
    setShowUploadTipsDialog(false);
    const openFileSelector = pendingUploadRef.current;
    pendingUploadRef.current = null;
    openFileSelector?.();
  };

  const handleUploadTipsClose = () => {
    setShowUploadTipsDialog(false);
    pendingUploadRef.current = null;
  };

  const openCreateDialog = () => {
    resetCreateForm();
    setEditingAvatar(null);
    setActiveAvatarTab('user');
    setIsCreateDialogOpen(true);
  };

  const handleOpenCreateDialog = async () => {
    if (!userId) {
      openCreateDialog();
      return;
    }
    if (checkingCreateAvatar) return;

    setCheckingCreateAvatar(true);
    try {
      const { data, error } = await checkCreateUserOfflineAvatar();
      if (isOfflineAvatarQuotaExceeded(error) || data?.ok === false) {
        toast({ type: 'error', message: t('createQuotaExceeded') });
        return;
      }
      if (error || !data) {
        toast({ type: 'error', message: t('createFailed') });
        return;
      }

      openCreateDialog();
    } catch {
      toast({ type: 'error', message: t('createFailed') });
    } finally {
      setCheckingCreateAvatar(false);
    }
  };

  const handleOpenEditDialog = (avatar: OfflineAvatarAsset) => {
    setEditingAvatar(avatar);
    setCreateName(avatar.name);
    setCreateDescription(avatar.description ?? '');
    setCreateVoiceId(avatar.voice_id ?? '');
    setCreateVoiceName(TTS_VOICE_MAP[avatar.voice_id ?? '']?.name || avatar.voice_id || '');
    setCreateImagePrompt({
      id: avatar.id,
      status: 'done',
      prompt: {
        sourceImage: avatar.live_image,
        sourceImagePreview: avatar.live_image,
        selectedImage: avatar.live_image,
        selectedImagePreview: avatar.live_image,
      },
    });
    setIsCreateDialogOpen(true);
  };

  const handleDeleteAvatar = (avatar: OfflineAvatarAsset) => {
    openConfirmDialog({
      title: t('deleteConfirmTitle'),
      description: t('deleteConfirmMessage'),
      primaryButtonText: t('deleteConfirmConfirm'),
      secondaryButtonText: t('deleteConfirmCancel'),
      onPrimaryAction: async () => {
        try {
          const { error } = await deleteUserOfflineAvatar({ path: { id: avatar.id } });
          if (error) {
            toast({ type: 'error', message: t('deleteFailed') });
            return;
          }
          if (selectedAvatar?.id === avatar.id) {
            onSelect(undefined);
          }
          await refreshUserAvatars();
          toast({ type: 'success', message: t('deleteSuccess') });
        } catch {
          toast({ type: 'error', message: t('deleteFailed') });
        }
      },
    });
  };

  const renderCreateCard = () => (
    <div className="relative flex aspect-square w-full rounded-12 border border-system-white08 bg-[#1A1C20] text-system-white transition-colors hover:border-system-white24 hover:bg-[#22252a]">
      <button
        type="button"
        aria-label={t('createTitle')}
        className="flex size-full items-center justify-center rounded-[11px]"
        onClick={handleOpenCreateDialog}
        disabled={checkingCreateAvatar}
      >
        {checkingCreateAvatar ? <Loading className="size-6 animate-spin" /> : <BaseLinePlus className="size-6" />}
      </button>
    </div>
  );

  const renderAvatarGrid = () => (
    <div className="grid h-full grid-cols-3 content-start gap-3 overflow-y-auto overflow-x-hidden p-3 pt-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {activeAvatarTab === 'user' ? renderCreateCard() : null}
      {activeLoading ? (
        <div
          className={cn(
            'flex h-full items-center justify-center rounded-12 bg-system-white04 text-system-white48',
            activeAvatarTab === 'user' ? 'col-span-2' : 'col-span-3',
          )}
        >
          <Loading className="size-6 animate-spin" />
        </div>
      ) : activeAvatars.length > 0 ? (
        activeAvatars.map((avatar) => (
          <OfflineAvatarCard
            key={avatar.id}
            avatar={avatar}
            selected={selectedAvatar?.id === avatar.id}
            onSelect={() => onSelect(selectedAvatar?.id === avatar.id ? undefined : avatar)}
            moreActionsLabel={
              activeAvatarTab === 'user' ? t('moreActions', { name: avatar.name || t('untitled') }) : undefined
            }
            editLabel={activeAvatarTab === 'user' ? t('edit') : undefined}
            deleteLabel={activeAvatarTab === 'user' ? t('delete') : undefined}
            onEdit={activeAvatarTab === 'user' ? () => handleOpenEditDialog(avatar) : undefined}
            onDelete={activeAvatarTab === 'user' ? () => handleDeleteAvatar(avatar) : undefined}
          />
        ))
      ) : (
        <div className={cn('h-full', activeAvatarTab === 'user' ? 'col-span-2' : 'col-span-3')}>
          <EmptyAssetState message={activeAvatarTab === 'user' ? t('emptyUser') : t('emptyPublic')} />
        </div>
      )}
    </div>
  );

  return (
    <section className="space-y-3">
      <ToolMediaUploadHeader title={title} titleClassName="text-sm" />
      <div className="relative h-[360px] overflow-hidden rounded-16 bg-[#1A1C20]">
        <Tabs
          value={activeAvatarTab}
          className="flex size-full flex-col"
          onValueChange={(value) => setActiveAvatarTab(value as OfflineAvatarAssetTab)}
        >
          <TabsList className="mx-3 mt-3 mb-3 flex h-fit w-fit gap-6 bg-transparent p-0">
            {OFFLINE_AVATAR_ASSET_TABS.map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className={cn(
                  'relative px-0 py-1 font-normal text-sm text-system-white64 hover:bg-transparent',
                  activeAvatarTab === tab &&
                    'before:-left-[1px] before:-right-[1px] font-medium text-system-white before:absolute before:bottom-0 before:h-[2px] before:rounded-full before:bg-system-white before:content-[""]',
                )}
              >
                {t(tab === 'user' ? 'userTab' : 'publicTab')}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="min-h-0 flex-1 overflow-hidden">{renderAvatarGrid()}</div>
        </Tabs>
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={handleCreateDialogOpenChange}>
        <DialogContent
          className="w-[836px] max-w-[calc(100%-32px)] border-white/10 bg-[#1c1c1c] p-6 text-white sm:w-[calc(100%-32px)]"
          closable={false}
          onClose={() => handleCreateDialogOpenChange(false)}
        >
          <div className="relative overflow-hidden">
            <div className="mb-5 flex items-center justify-between gap-3">
              <DialogTitle className="text-left text-white text-xl leading-none">
                {t(editingAvatar ? 'editTitle' : 'createTitle')}
              </DialogTitle>
              <DialogClose
                className="relative inset-auto top-auto right-auto flex size-7 shrink-0 text-white hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => handleCreateDialogOpenChange(false)}
                disabled={savingAvatar}
              />
            </div>

            <div className="flex gap-6 sm:flex-col">
              <div className="flex min-w-0 flex-1 flex-col gap-2 sm:w-full sm:flex-none">
                <ToolMediaUpload
                  type="image"
                  isMobile={false}
                  imagePrompt={createImagePrompt}
                  onImageUpload={handleCreateImageUpload}
                  onRequestUpload={handleRequestCreateImageUpload}
                  onRemoveMedia={() => setCreateImagePrompt(null)}
                  getSelectedImage={getSelectedImage}
                  maxFileSize={maxImageFileSize}
                  allowTempMode={false}
                  surfaceSize="fill"
                  className="h-[466px] w-full sm:aspect-square sm:h-auto"
                  surfaceClassName={cn(
                    'h-full min-h-0 rounded-2xl border bg-white/5 p-0 transition-colors',
                    createImagePreview ? 'border-white/20' : 'border-white/10 hover:!bg-white/[0.07]',
                  )}
                  uploadActionClassName="h-full w-full flex-col gap-3 rounded-2xl p-0 [&>span:last-child]:hidden"
                  uploadIcon={
                    <span className="flex flex-col items-center gap-3">
                      <BaseLinePlus className="size-7 text-system-white" />
                      <span className="text-base text-white/40">{t('pickPhoto')}</span>
                    </span>
                  }
                  header={null}
                />
                <p className="text-center text-white/45 text-xs">{t('uploadCopyrightNotice')}</p>
              </div>

              <div className="flex w-[324px] shrink-0 flex-col gap-6 sm:w-full">
                <label className="block">
                  <div className="mb-2 flex items-center justify-between text-sm text-white/60">
                    <span>{t('nameLabel')}</span>
                    <span className="text-white/30 text-xs">
                      {createName.length}/{createNameLimit}
                    </span>
                  </div>
                  <input
                    value={createName}
                    maxLength={createNameLimit}
                    placeholder={t('namePlaceholder')}
                    className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-white outline-none transition-colors placeholder:text-white/40 focus:border-white/40 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={savingAvatar}
                    onChange={(event) => setCreateName(event.target.value)}
                  />
                </label>

                <label className="block">
                  <div className="mb-2 flex items-center justify-between text-sm text-white/60">
                    <span>{t('descriptionLabel')}</span>
                    <span className="text-white/30 text-xs">
                      {createDescription.length}/{MAX_CREATE_DESCRIPTION}
                    </span>
                  </div>
                  <textarea
                    value={createDescription}
                    maxLength={MAX_CREATE_DESCRIPTION}
                    rows={9}
                    placeholder={t('descriptionPlaceholder')}
                    className="w-full resize-none rounded-xl border border-white/10 bg-white/5 pt-3 pr-2 pb-3 pl-4 text-sm text-white outline-none transition-colors placeholder:text-white/40 focus:border-white/40 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={savingAvatar}
                    onChange={(event) => setCreateDescription(event.target.value)}
                  />
                </label>

                <div>
                  <p className="mb-2 text-sm text-white/60">{t('voiceLabel')}</p>
                  <VoiceSelectionDialog
                    open={isVoiceDialogOpen}
                    onOpenChange={setIsVoiceDialogOpen}
                    title={t('voiceLabel')}
                    value={createVoiceId}
                    onConfirm={(nextVoiceId, nextVoice) => {
                      setCreateVoiceId(nextVoiceId);
                      setCreateVoiceName(nextVoice?.name || '');
                    }}
                    onResolvedVoice={(voice) => setCreateVoiceName(voice.name)}
                    allowNoVoice
                    noSoundLabel={t('noVoice')}
                    voiceInfoEditFieldSuffix="offline-avatar"
                    trigger={
                      <button
                        type="button"
                        className={cn(
                          'flex h-12 w-full items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 text-sm transition-colors hover:border-white/20',
                          createVoiceId ? 'text-white' : 'text-white/50 hover:text-white/70',
                        )}
                        disabled={savingAvatar}
                      >
                        <span className="min-w-0 flex-1 truncate text-left">{createVoiceName || t('chooseVoice')}</span>
                        <BaseLineRight className="size-4 shrink-0 text-white/40" />
                      </button>
                    }
                  />
                </div>

                <div className="mt-auto">
                  <Button
                    type="button"
                    variant="solid"
                    onClick={editingAvatar ? handleUpdateAvatar : handleCreateAvatar}
                    loading={savingAvatar}
                    disabled={isCreateImageUploading || savingAvatar || !canCreateAvatar}
                    className="h-11 w-full rounded-xl bg-white px-8 font-medium text-black text-sm hover:bg-white disabled:opacity-50"
                  >
                    {t(editingAvatar ? 'save' : 'create')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PhotoUploadTipsDialog
        open={showUploadTipsDialog}
        title={tUploadTips('uploadTips.title')}
        orLabel={tUploadTips('uploadTips.or')}
        tip1Title={tUploadTips('uploadTips.tip1Title')}
        tip1Description={tUploadTips('uploadTips.tip1Description')}
        tip2Title={tUploadTips('uploadTips.tip2Title')}
        tip2Description={tUploadTips('uploadTips.tip2Description')}
        tip3Title={tUploadTips('uploadTips.tip3Title')}
        tip3Description={tUploadTips('uploadTips.tip3Description')}
        confirmText={tUploadTips('uploadTips.confirm')}
        onConfirm={handleUploadTipsConfirm}
        onClose={handleUploadTipsClose}
      />
    </section>
  );
};
